import { useState, useEffect } from 'react'
import { Edit, Eye, EyeOff, Save, X, TrendingUp } from 'lucide-react'
import { statsAPI } from '../services/statsService'

function AdminStats() {
  const [stats, setStats] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingStat, setEditingStat] = useState(null)
  const [editForm, setEditForm] = useState({
    title: '',
    value: '',
    is_visible: true
  })

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    try {
      setLoading(true)
      const data = await statsAPI.getAllStats()
      setStats(data)
      setError(null)
    } catch (err) {
      console.error('İstatistikler yüklenirken hata:', err)
      setError('İstatistikler yüklenirken bir hata oluştu.')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (stat) => {
    setEditingStat(stat)
    setEditForm({
      title: stat.title,
      value: stat.value,
      is_visible: stat.is_visible
    })
  }

  const handleCancelEdit = () => {
    setEditingStat(null)
    setEditForm({
      title: '',
      value: '',
      is_visible: true
    })
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSave = async () => {
    try {
      await statsAPI.updateStat(editingStat.id, editForm)
      await fetchStats()
      setEditingStat(null)
      setEditForm({
        title: '',
        value: '',
        is_visible: true
      })
    } catch (err) {
      console.error('İstatistik güncellenirken hata:', err)
      setError('İstatistik güncellenirken bir hata oluştu.')
    }
  }

  const handleToggleVisibility = async (stat) => {
    try {
      await statsAPI.updateStat(stat.id, {
        ...stat,
        is_visible: !stat.is_visible
      })
      await fetchStats()
    } catch (err) {
      console.error('İstatistik durumu güncellenirken hata:', err)
      setError('İstatistik durumu güncellenirken bir hata oluştu.')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">İstatistikler yükleniyor...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">İstatistik Yönetimi</h1>
            <p className="text-gray-600 mt-2">Site istatistiklerini düzenleyin ve yönetin</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div
              key={stat.id}
              className={`bg-white rounded-lg shadow-lg p-6 border-2 ${
                !stat.is_visible ? 'border-gray-200 opacity-60' : 'border-transparent'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{stat.title}</h3>
                    <p className="text-2xl font-bold text-blue-600">{stat.value}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleVisibility(stat)}
                    className={`p-1 rounded ${
                      stat.is_visible 
                        ? 'text-green-600 hover:text-green-700' 
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title={stat.is_visible ? 'Gizle' : 'Göster'}
                  >
                    {stat.is_visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  
                  <button
                    onClick={() => handleEdit(stat)}
                    className="p-1 text-blue-600 hover:text-blue-700 rounded"
                    title="Düzenle"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Edit Form */}
              {editingStat && editingStat.id === stat.id && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Başlık
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={editForm.title}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Değer
                      </label>
                      <input
                        type="text"
                        name="value"
                        value={editForm.value}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_visible"
                        checked={editForm.is_visible}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        Görünür
                      </label>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSave}
                        className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                      >
                        <Save className="h-3 w-3" />
                        <span>Kaydet</span>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center space-x-1 px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                      >
                        <X className="h-3 w-3" />
                        <span>İptal</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Bilgi</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• İstatistiklerin başlığı ve değeri düzenlenebilir</li>
            <li>• Görünürlük durumu değiştirilebilir</li>
            <li>• İkonlar sabit kalır ve değiştirilemez</li>
            <li>• Gizli istatistikler site üzerinde görünmez</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default AdminStats 